/**
 * Pilha de PUSH na tela (ver push.ts). Montado uma vez no RootLayout.
 */
import { cx } from "@/ui/cx";
import { MaterialCommunityIcons } from "@/ui/icons";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { push, type Push, type TomPush } from "./push";
import s from "./PushHost.module.css";

const ICONE_DO_TOM: Record<TomPush, string> = {
  info: "bell-ring-outline",
  sucesso: "check-circle-outline",
  alerta: "alert-outline",
  erro: "close-circle-outline",
};

/** Arrastou mais que isto para o lado: dispensa. */
const ARRASTO_FECHA_PX = 90;

function quando(criadoEm: number, agora: number): string {
  const min = Math.floor((agora - criadoEm) / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `há ${min} min`;
  return `há ${Math.floor(min / 60)} h`;
}

function CartaoPush({ p, agora }: { p: Push; agora: number }) {
  const [pausado, setPausado] = useState(false);
  const [arrasto, setArrasto] = useState(0);
  const inicio = useRef<{ x: number; id: number } | null>(null);
  const tom = p.tom ?? "info";
  const dura = !!p.duracaoMs && p.duracaoMs > 0 && !p.carregando;

  const onPointerDown = (e: React.PointerEvent) => {
    // Só arrasta pelo corpo do cartão (botões continuam sendo clique).
    if ((e.target as HTMLElement).closest("button")) return;
    inicio.current = { x: e.clientX, id: e.pointerId };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    setPausado(true);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (inicio.current?.id !== e.pointerId) return;
    setArrasto(e.clientX - inicio.current.x);
  };
  const soltar = (e: React.PointerEvent) => {
    if (inicio.current?.id !== e.pointerId) return;
    inicio.current = null;
    setPausado(false);
    if (Math.abs(arrasto) > ARRASTO_FECHA_PX) push.fechar(p.id);
    else setArrasto(0);
  };

  return (
    <div
      className={cx(s.cartao, s[`tom_${tom}`], p.saindo && s.saindo, (pausado || !dura) && s.pausado)}
      role={tom === "erro" || tom === "alerta" ? "alert" : "status"}
      onMouseEnter={() => setPausado(true)}
      onMouseLeave={() => setPausado(false)}
      onFocus={() => setPausado(true)}
      onBlur={() => setPausado(false)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={soltar}
      onPointerCancel={soltar}
      style={
        arrasto
          ? {
              transform: `translateX(${arrasto}px)`,
              opacity: Math.max(0.2, 1 - Math.abs(arrasto) / 260),
              transition: "none",
            }
          : undefined
      }
    >
      <span className={s.faixa} aria-hidden="true" />
      <span className={s.cabecalho}>
        <img className={s.app} src="/pwa/icon-192.png" alt="" />
        <span className={s.appNome}>Tirvu+</span>
        <span className={s.quando}>· {quando(p.criadoEm, agora)}</span>
        <button type="button" className={s.fechar} onClick={() => push.fechar(p.id)} aria-label="Fechar aviso">
          <MaterialCommunityIcons name="close" size={16} color="#7b939c" />
        </button>
      </span>
      <span className={s.corpo}>
        <span className={s.icone}>
          {p.carregando ? (
            <span className={s.girando} aria-label="Carregando" />
          ) : (
            <MaterialCommunityIcons name={(p.icone ?? ICONE_DO_TOM[tom]) as any} size={22} color="#ffffff" />
          )}
        </span>
        <span className={s.textos}>
          <span className={s.titulo}>{p.titulo}</span>
          {p.mensagem ? <span className={s.mensagem}>{p.mensagem}</span> : null}
        </span>
      </span>
      {p.acoes && p.acoes.length > 0 ? (
        <span className={s.acoes}>
          {p.acoes.map((a) => (
            <button
              key={a.rotulo}
              type="button"
              className={cx(s.acao, a.principal && s.acaoPrincipal)}
              disabled={p.carregando}
              onClick={() => {
                try {
                  a.aoTocar?.();
                } finally {
                  if (!a.manterAberto) push.fechar(p.id);
                }
              }}
            >
              {a.rotulo}
            </button>
          ))}
        </span>
      ) : null}
      {dura ? (
        <span
          key={p.versao}
          className={s.tempo}
          style={{ animationDuration: `${p.duracaoMs}ms` }}
          onAnimationEnd={() => push.fechar(p.id)}
          aria-hidden="true"
        />
      ) : null}
    </div>
  );
}

export function PushHost() {
  const lista = useSyncExternalStore(push.observar, push.lista, push.lista);
  // "agora" / "há 3 min" se atualiza enquanto houver push na tela.
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    if (lista.length === 0) return;
    setAgora(Date.now());
    const t = setInterval(() => setAgora(Date.now()), 30000);
    return () => clearInterval(t);
  }, [lista.length]);

  if (typeof document === "undefined") return null;
  return createPortal(
    <div className={s.area} aria-live="polite">
      {lista.map((p) => (
        <CartaoPush key={p.id} p={p} agora={agora} />
      ))}
    </div>,
    document.body,
  );
}
